import React, { useState, useRef, useEffect, useCallback } from 'react';
import SdmtSymbol from './SdmtSymbol';
import { Mic, Keyboard } from 'lucide-react';
import useTimerState from '../../../components/primitives/useTimerState';
import CountdownGate from '../shared/CountdownGate';
import ClinicalTimer from '../shared/ClinicalTimer';

const TEST_DURATION_MS = 90000;
const INTERVAL_BUCKETS_MS = [30000, 60000];

const wordToDigit = (word) => {
  const map = {
    'один': '1', '1': '1', 'два': '2', '2': '2', 'три': '3', '3': '3',
    'четыре': '4', '4': '4', 'пять': '5', '5': '5', 'шесть': '6', '6': '6',
    'семь': '7', '7': '7', 'восемь': '8', '8': '8', 'девять': '9', '9': '9'
  };
  const cleanWord = word.replace(/[.,!?]/g, '');
  return map[cleanWord] || null;
};

const DUPLICATE_ECHO_WINDOW_MS = 600;

export default function CodingV2({ question, transcript, status, mode, onChoiceClick, startListening, speak }) {
  const { key, sequence } = question;
  const [inputs, setInputs] = useState(Array(sequence.length).fill(''));
  const [currentIndex, setCurrentIndex] = useState(0);
  const [phase, setPhase] = useState('instruction');

  const startTimeRef = useRef(0);
  const dynamicsRef = useRef([0, 0, 0]);
  const isFinishedRef = useRef(false);
  const inputsRef = useRef(inputs);
  const currentIndexRef = useRef(0);
  const phaseRef = useRef(phase);
  const onChoiceClickRef = useRef(onChoiceClick);
  const startListeningRef = useRef(startListening);

  const committedCountRef = useRef(0);
  const lastCommittedDigitRef = useRef(null);
  const lastCommitTimeRef = useRef(0);

  useEffect(() => { inputsRef.current = inputs; }, [inputs]);
  useEffect(() => { currentIndexRef.current = currentIndex; }, [currentIndex]);
  useEffect(() => { phaseRef.current = phase; }, [phase]);
  useEffect(() => { onChoiceClickRef.current = onChoiceClick; }, [onChoiceClick]);
  useEffect(() => { startListeningRef.current = startListening; }, [startListening]);

  const handleFinish = useCallback(() => {
    if (isFinishedRef.current) return;
    isFinishedRef.current = true;

    const currentInputs = inputsRef.current;
    let score = 0, errors = 0, omissions = 0;

    sequence.forEach((symId, i) => {
      const ans = currentInputs[i];
      const correct = key[symId];
      if (ans === correct) score++;
      else if (ans === '') omissions++;
      else errors++;
    });

    const payload = JSON.stringify({
      score,
      errors,
      omissions,
      intervals: dynamicsRef.current,
      form: mode === 'show' ? 'written' : 'oral'
    });
    onChoiceClickRef.current(payload);
  }, [key, sequence, mode]);

  const timer = useTimerState({ duration: TEST_DURATION_MS, onExpire: handleFinish });
  const timerStartRef = useRef(timer.start);
  useEffect(() => { timerStartRef.current = timer.start; }, [timer.start]);

  useEffect(() => {
    if (status === 'show_options' && phase === 'instruction') setPhase('countdown');
  }, [status, phase]);

  // Здесь жил главный баг v2: setPhase('test') вызывался ДО speak('Начинайте'),
  // эффект с зависимостями [phase, mode] перезапускался, его cleanup ставил
  // cancelled = true, и колбэк окончания речи молча не вызывал
  // startListening() — микрофон не включался НИКОГДА, без ошибок в консоли.
  // Теперь отсчёт живёт в CountdownGate и вызывает onReady ровно один раз,
  // после реального окончания речи.
  const handleCountdownReady = useCallback(() => {
    startTimeRef.current = Date.now();
    timerStartRef.current(TEST_DURATION_MS);
    if (mode === 'say' && startListeningRef.current) startListeningRef.current();
    setPhase('test');
  }, [mode]);

  const commitDigit = useCallback((value) => {
    if (isFinishedRef.current || phaseRef.current !== 'test') return;
    if (!/^[1-9]$/.test(value)) return;

    const idx = currentIndexRef.current;
    if (idx >= sequence.length) return;

    const next = [...inputsRef.current];
    next[idx] = value;
    inputsRef.current = next;
    setInputs(next);

    if (value === key[sequence[idx]]) {
      const elapsed = Date.now() - startTimeRef.current;
      if (elapsed < INTERVAL_BUCKETS_MS[0]) dynamicsRef.current[0]++;
      else if (elapsed < INTERVAL_BUCKETS_MS[1]) dynamicsRef.current[1]++;
      else dynamicsRef.current[2]++;
    }

    if (idx < sequence.length - 1) {
      const nextIdx = idx + 1;
      currentIndexRef.current = nextIdx;
      setCurrentIndex(nextIdx);
    } else {
      handleFinish();
    }
  }, [key, sequence, handleFinish]);

  useEffect(() => {
    if (mode !== 'say' || phase !== 'test') return;
    if (!transcript) return;

    const words = transcript.toLowerCase().split(/\s+/).filter(Boolean);
    const digitWords = words.map(wordToDigit).filter(Boolean);
    console.log(`[SDMT] @${((Date.now() - startTimeRef.current) / 1000).toFixed(1)}с слов=${words.length} цифр=${digitWords.length} засчитано=${committedCountRef.current} последнее="${words[words.length - 1]}"`);

    const newDigits = digitWords.slice(committedCountRef.current);
    if (newDigits.length === 0) return;
    committedCountRef.current += newDigits.length;

    const now = Date.now();
    for (const digit of newDigits) {
      const isSuspiciousEcho = digit === lastCommittedDigitRef.current
        && now - lastCommitTimeRef.current < DUPLICATE_ECHO_WINDOW_MS;
      if (isSuspiciousEcho) continue;
      lastCommittedDigitRef.current = digit;
      lastCommitTimeRef.current = now;
      commitDigit(digit);
    }
  }, [transcript, mode, phase, commitDigit]);

  useEffect(() => {
    if (mode !== 'show' || phase !== 'test') return undefined;
    const handleKeyDown = (e) => {
      if (!/^[1-9]$/.test(e.key)) return;
      e.preventDefault();
      commitDigit(e.key);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [mode, phase, commitDigit]);

  if (phase === 'countdown') {
    return <CountdownGate speak={speak} onReady={handleCountdownReady} />;
  }

  const currentSymbol = sequence[currentIndex];

      return (
    <div className="flex h-screen w-full max-w-5xl flex-col items-center justify-center gap-6">
      {/* Ключ — по центру, между ним и полем с символом больше ничего нет */}
      <div className="flex flex-wrap justify-center gap-4 rounded-2xl border border-border bg-secondary p-4 shadow-md">
        {Object.keys(key).map((symId) => (
          <div key={symId} className="flex w-14 flex-col items-center gap-2 border-r border-border px-2 last:border-r-0">
            <span className="flex h-10 items-center justify-center text-primary"><SdmtSymbol id={symId} size={32} /></span>
            <div className="flex h-10 w-full items-center justify-center border-b-2 border-border text-lg font-bold text-muted-foreground">{key[symId]}</div>
          </div>
        ))}
      </div>

      <div className="flex h-[200px] w-[200px] items-center justify-center rounded-3xl border-2 border-border bg-white p-2 shadow-2xl">
        <SdmtSymbol id={currentSymbol} size={120} />
      </div>

      {/* Таймер — под полем с символом */}
      {phase === 'test' && (
        <ClinicalTimer remainingSec={timer.remainingSec} percent={timer.percent} />
      )}

      <p className="text-sm text-muted-foreground">Символ {currentIndex + 1} из {sequence.length}</p>

      {mode === 'show' && (
        <div className="z-30 rounded-2xl border-2 border-border bg-background p-4 shadow-xl">
          <p className="mb-2 flex items-center justify-center gap-2 text-xs text-muted-foreground">
            <Keyboard className="size-4" /> Печатайте с клавиатуры или нажимайте кнопки
          </p>
          <div className="mx-auto grid max-w-[240px] grid-cols-3 gap-3">
            {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((num) => (
              <button
                key={num}
                onClick={() => commitDigit(num)}
                disabled={isFinishedRef.current || phase !== 'test'}
                className="flex h-16 w-16 items-center justify-center rounded-2xl border-2 border-border bg-secondary text-2xl font-bold text-primary shadow-sm transition-all hover:bg-accent/10 active:scale-95"
              >
                {num}
              </button>
            ))}
          </div>
        </div>
      )}

      {mode === 'say' && (
        <div className="z-30 flex flex-col items-center gap-2 rounded-2xl border-2 border-border bg-background p-4 shadow-xl">
          <Mic className="size-12 animate-bounce text-red-500" />
          <p className="text-sm font-medium text-muted-foreground">Называйте цифру</p>
        </div>
      )}
    </div>
  );
}
